// Serializes the map state into the URL hash so links are shareable.
import { get } from "svelte/store";
import { vec2, mat3 } from "gl-matrix";

import {
  seed,
  noiseFactor,
  reliefFactor,
  borderFactor,
  waveStyle,
  waveOpacity,
  waveSpeed,
  waveScale,
  waveThickness,
  vignette,
  grain,
  paletteName,
  palette,
  overlays,
} from "../stores/globalControls.js";

const VERSION = 3;
const PREFIX = "#m=";
const SYNC_DELAY = 400;

const STORES = [
  seed,
  noiseFactor,
  reliefFactor,
  borderFactor,
  waveStyle,
  waveOpacity,
  waveSpeed,
  waveScale,
  waveThickness,
  vignette,
  grain,
  paletteName,
  palette,
  overlays,
];

const round = (n, d = 4) => Math.round(n * 10 ** d) / 10 ** d;

function makeDocument() {
  return { position: vec2.create(), extent: vec2.create(), listeners: {} };
}

function serialize() {
  const all = get(overlays);
  const state = {
    v: VERSION,
    s: get(seed), // full precision: the shader hashes the seed, so rounding changes the whole map
    n: round(get(noiseFactor)),
    r: round(get(reliefFactor)),
    b: round(get(borderFactor)),
    vg: round(get(vignette)),
    gr: round(get(grain)),
    w: [
      get(waveStyle),
      round(get(waveOpacity)),
      round(get(waveSpeed)),
      round(get(waveScale)),
      round(get(waveThickness)),
    ],
    p: get(paletteName),
    inf: all
      .filter((o) => o.type === "topoInfluence")
      .map((o) => [
        round(o.factor),
        round(o.transform[0]),
        round(o.transform[6]),
        round(o.transform[7]),
        o.color,
      ]),
    lab: all
      .filter((o) => o.type === "label")
      .map((o) => ({
        t: o.text,
        x: round(o.transform[6]),
        y: round(o.transform[7]),
        fs: round(o.fontSizeRem),
        ls: round(o.letterSpacingRem),
        z: round(o.zooming),
        r: round(o.rotationDeg),
        a: o.textAlign,
        an: o.anchor,
      })),
    stk: all
      .filter((o) => o.type === "sticker")
      .map((o) => ({
        i: o.icon,
        x: round(o.transform[6]),
        y: round(o.transform[7]),
        s: round(o.sizeRem),
        z: round(o.zooming),
        r: round(o.rotationDeg),
        an: o.anchor,
        c: o.color,
      })),
  };
  if (get(paletteName) === "custom") {
    state.pc = get(palette);
  }
  return state;
}

function applyState(s) {
  if (typeof s.s === "number") seed.set(s.s);
  if (typeof s.n === "number") noiseFactor.set(s.n);
  if (typeof s.r === "number") reliefFactor.set(s.r);
  if (typeof s.b === "number") borderFactor.set(s.b);
  if (typeof s.vg === "number") vignette.set(s.vg);
  if (typeof s.gr === "number") grain.set(s.gr);
  if (Array.isArray(s.w)) {
    waveStyle.set(s.w[0]);
    waveOpacity.set(s.w[1]);
    waveSpeed.set(s.w[2]);
    waveScale.set(s.w[3]);
    waveThickness.set(s.w[4]);
  }
  if (s.p) paletteName.set(s.p);
  if (Array.isArray(s.pc)) palette.set(s.pc);

  const restored = [];
  (s.inf || []).forEach(([factor, scale, x, y, color], i) => {
    restored.push({
      type: "topoInfluence",
      name: "topoInfluence" + i,
      factor,
      color: color || "#ffffff",
      transform: mat3.fromValues(scale, 0, 0, 0, scale, 0, x, y, scale),
      document: makeDocument(),
    });
  });
  (s.lab || []).forEach((l, i) => {
    restored.push({
      type: "label",
      name: "label" + i,
      text: l.t,
      fontSizeRem: l.fs,
      letterSpacingRem: l.ls,
      zooming: l.z,
      rotationDeg: l.r,
      textAlign: l.a,
      anchor: l.an,
      transform: mat3.fromValues(1, 0, 0, 0, 1, 0, l.x, l.y, 1),
      document: makeDocument(),
    });
  });
  (s.stk || []).forEach((k, i) => {
    restored.push({
      type: "sticker",
      name: "sticker" + i,
      icon: k.i,
      color: k.c || "#ffffff",
      sizeRem: k.s,
      zooming: k.z,
      rotationDeg: k.r,
      anchor: k.an,
      transform: mat3.fromValues(1, 0, 0, 0, 1, 0, k.x, k.y, 1),
      document: makeDocument(),
    });
  });
  if (restored.length) {
    overlays.set(restored);
  }
}

function encode(obj) {
  const bytes = new TextEncoder().encode(JSON.stringify(obj));
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decode(str) {
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return JSON.parse(new TextDecoder().decode(bytes));
}

export function loadFromUrl() {
  if (!location.hash.startsWith(PREFIX)) {
    return false;
  }
  try {
    applyState(decode(location.hash.slice(PREFIX.length)));
    return true;
  } catch (e) {
    console.warn("Could not load map from URL:", e);
    return false;
  }
}

let lastEncoded = null;
let timer = null;

function syncToUrl() {
  const encoded = encode(serialize());
  if (encoded === lastEncoded) {
    return;
  }
  lastEncoded = encoded;
  history.replaceState(null, "", PREFIX + encoded);
}

function scheduleSync() {
  if (timer !== null) {
    clearTimeout(timer);
  }
  timer = setTimeout(syncToUrl, SYNC_DELAY);
}

// Write any pending state to the URL immediately, bypassing the debounce, so
// callers (e.g. the Share button) can read an up-to-date location.href.
export function flushUrl() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  syncToUrl();
}

export function startSync() {
  const unsubscribers = STORES.map((store) => store.subscribe(scheduleSync));
  return () => {
    unsubscribers.forEach((u) => u());
    if (timer !== null) {
      clearTimeout(timer);
    }
  };
}
